import { Injectable } from '@angular/core';
import { combineLatest, distinctUntilChanged, map, Observable, Subject, Subscription } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { Context } from '../models/context';
import { ISession, Session } from '../models/session';
import { User } from '../models/user';
import { SpaceRepository } from '../state/space.repository';
import { PanelView } from '../sessions/panel/panel.component';
import { Section } from '../sessions/panel/participants-manager/participants-manager.component';
import { KeyboardKeys } from '../sessions/session/wb-main-toolbar-v2/services/main-toolbar.service';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { UserService } from './user.service';
import { KeyboardShortcutsCheckService } from './keyboard-shortcuts-check.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { FeatureManagementService } from './feature-management.service';

@Injectable({
  providedIn: 'root',
})
export class ChildModeService {
  /**
   * Emits when the Child mode row in Participant Manager → Permissions should play its highlight
   * animation (e.g. opening that section from chat or the child mode indicator).
   */
  readonly flashChildModePermissionsRow = new Subject<void>();

  /** Current child mode state for synchronous checks (e.g. keyboard shortcuts). */
  private _isChildModeActive = false;

  private sessionSubscriptions = new Subscription();

  constructor(
    private readonly spaceRepo: SpaceRepository,
    private readonly featureManagementService: FeatureManagementService,
    private readonly sessionSharedDataService: SessionSharedDataService,
    private readonly keyboardShortcutsCheckService: KeyboardShortcutsCheckService,
    private readonly userService: UserService,
  ) {}

  // Whether child mode is turned ON for the space — used to drive the checkbox
  // in the participant manager. Visible to all roles (hosts need to see the setting).
  readonly isChildModeEnabled$: Observable<boolean> = combineLatest([
    this.featureManagementService.childModeCapabilityEnabled$,
    this.spaceRepo.activeSpace$,
  ]).pipe(
    map(([isCapabilityEnabled, space]) => {
      if (!isCapabilityEnabled) {
        return false;
      }
      return space?.settings?.enableChildMode === true;
    }),
    distinctUntilChanged(),
  );

  // Whether child mode is actively applied for the current user — used to drive
  // UI simplifications (toolbar, top bar, video controls).
  // Hosts and owners always see the full UI regardless of the setting.
  readonly isChildModeActive$: Observable<boolean> = combineLatest([
    this.isChildModeEnabled$,
    this.spaceRepo.isCurrentUserHost$,
  ]).pipe(
    map(([isChildModeEnabled, isHost]) => isChildModeEnabled && !isHost),
    distinctUntilChanged(),
  );

  /**
   * Start subscriptions that should only run while the user is in a session/space.
   * Call from session component when entering a space.
   */
  startSessionSubscriptions(): void {
    this.stopSessionSubscriptions();

    this.sessionSubscriptions.add(
      this.isChildModeActive$
        .pipe(startWith(this._isChildModeActive))
        .subscribe((active) => (this._isChildModeActive = active)),
    );

    this.sessionSubscriptions.add(
      this.isChildModeActive$.pipe(filter((isActive) => isActive)).subscribe(() => {
        this.keyboardShortcutsCheckService.closeOpenMenus();
        this.sessionSharedDataService.changeLeftPanelView.next(undefined);
        this.sessionSharedDataService.changeRightPanelView.next(undefined);
      }),
    );
  }

  /**
   * Stop session-scoped subscriptions and reset sync state.
   * Call from session component when leaving a space (e.g. ngOnDestroy).
   */
  stopSessionSubscriptions(): void {
    this.sessionSubscriptions.unsubscribe();
    this.sessionSubscriptions = new Subscription();
    this._isChildModeActive = false;
  }

  /** Synchronous check for child mode (e.g. in shortcut handlers). */
  get isChildModeActive(): boolean {
    return this._isChildModeActive;
  }

  /**
   * Tool shortcut keys that remain enabled in child mode (pointer, pan, pen, highlighter, eraser).
   * Keys are the string values used by the main toolbar (e.g. 'space', 'no_shift.m').
   */
  private readonly allowedToolShortcutsInChildMode = new Set<string>([
    KeyboardKeys.POINTER,
    KeyboardKeys.SPACE,
    KeyboardKeys.PAN,
    KeyboardKeys.DRAW,
    KeyboardKeys.HIGHLIGHT,
    KeyboardKeys.MAGIC_INK,
    KeyboardKeys.ERASER,
    KeyboardKeys.DELETE,
  ]);

  /**
   * Whether a tool shortcut should be handled when in child mode.
   * When not in child mode, returns true. When in child mode, returns true only for allowed shortcuts (pan, pen, highlighter, eraser).
   */
  isShortcutEnabledInChildMode(shortcutKey: string): boolean {
    if (!this.isChildModeActive) {
      return true;
    }
    return this.allowedToolShortcutsInChildMode.has(shortcutKey);
  }

  /**
   * Opens the right panel to Participants Manager on the Permissions tab and flashes the
   * child mode row (next tick so the view can render first).
   */
  openParticipantsManagerChildModePermissions(): void {
    this.sessionSharedDataService.changeParticipantsManagerSection.next(Section.PERMISSIONS);
    if (
      this.sessionSharedDataService.changeRightPanelView.value !== PanelView.participantsManager
    ) {
      this.sessionSharedDataService.changeRightPanelView.next(PanelView.participantsManager);
    }

    // This timeout is necessary to ensure that the view is rendered before the flash animation starts.
    modifiedSetTimeout(
      () => {
        this.flashChildModePermissionsRow.next();
      },
      0,
      true,
    );
  }

  /**
   * Private / DM chat only — not used for shared space chat (see chat-window + isChildModeEnabled$).
   * When child mode is on: participants cannot compose. Hosts may compose only when the
   * conversation has at least one counterparty and every counterparty is a host of the active
   * space. Space-wide chat, groups, and DMs with non-hosts stay blocked.
   */
  isChatComposerBlockedInChildMode$(
    context$: Observable<Context | null | undefined>,
  ): Observable<boolean> {
    return combineLatest([
      this.isChildModeEnabled$,
      this.spaceRepo.activeSpace$,
      this.userService.user.pipe(
        map((w) => w?.user as User | undefined),
        distinctUntilChanged((a, b) => a?._id === b?._id),
      ),
      context$,
    ]).pipe(
      map(([childModeEnabled, space, user, context]) => {
        if (!childModeEnabled) {
          return false;
        }
        const isHost = Session.isOwnedByUser(space, user);
        if (!isHost || !user?._id) {
          return true;
        }
        return !this.isUserHostMessagingAnotherHost(space, user._id, context ?? undefined);
      }),
      distinctUntilChanged(),
    );
  }

  private isUserHostMessagingAnotherHost(
    space: ISession | null | undefined,
    currentUserId: string,
    context: Context | undefined,
  ): boolean {
    if (!space || !context) {
      return false;
    }
    if (context.session) {
      return false;
    }
    if (context.group) {
      return false;
    }
    if (!context.users?.length) {
      return false;
    }
    const hostIds = Session.getSpaceHostsIds(space);
    if (!hostIds.has(currentUserId)) {
      return false;
    }
    const counterpartyIds = context.users.filter((id) => id !== currentUserId);
    if (counterpartyIds.length === 0) {
      return false;
    }
    return counterpartyIds.every((id) => hostIds.has(id));
  }
}
